import * as React from 'react';

import {Card} from '../card/card';
import {colors} from '../colors/colors';
import type {ColorName} from '../colors/colors';
import {CardWidgetEmptyState} from '../empty-states/card-widget-empty-state';
import {ChartLineRegularReportIcon} from '../icon/svg/chart-line-regular-report-icon';
import {ListView} from '../list-view/list-view';
import {LoadingPage} from '../loading-page/loading-page';
import {Body} from '../typography/body';

import {getLeaderboardPositions} from './helpers';
import {LeaderboardRow} from './leaderboard-row';
import type {EntityLeaderboardRows, OtherLeaderboardRows} from './types';

import styles from './leaderboard.module.css';

type Props = {
    title: string;
    subtitle?: string;
    rowsData: EntityLeaderboardRows | OtherLeaderboardRows;
    loadingProps?: {
        isLoading?: boolean;
        fill?: ColorName;
    };
    hideMedal?: boolean;
    emptyStateProps?: {
        text: string;
        icon?: React.ComponentType<any>;
        iconSize?: number;
    };
    hasConsecutivePositions?: boolean;
};

export function Leaderboard(props: Props) {
    const positions = getLeaderboardPositions(props.rowsData, props.hasConsecutivePositions);

    let leaderboardContent = null;
    if (props.loadingProps && props.loadingProps.isLoading) {
        leaderboardContent = (
            <div className={styles['loading-container']}>
                <LoadingPage
                    padding={0}
                    fill={props.loadingProps.fill ? colors[props.loadingProps.fill] : undefined}
                />
            </div>
        );
    } else if (props.rowsData.length === 0) {
        leaderboardContent = (
            <div className={styles['empty-container']}>
                <CardWidgetEmptyState
                    text={props.emptyStateProps?.text ?? 'No data'}
                    icon={props.emptyStateProps?.icon ?? ChartLineRegularReportIcon}
                    iconSize={props.emptyStateProps?.iconSize ?? 20}
                />
            </div>
        );
    } else {
        leaderboardContent = (
            <ListView
                isFullWidth={true}
                collection={props.rowsData}
                renderRow={(data, index) => (
                    <LeaderboardRow
                        key={data.id}
                        // @ts-expect-error
                        position={positions[index]}
                        data={data}
                        hideMedal={props.hideMedal}
                    />
                )}
            />
        );
    }

    return (
        <Card
            hasBorder={true}
            borderRadius={8}
            hasShadow={true}
            borderColor={colors.buttonSecondaryDk}
        >
            <div className='flex flex-dir-col full-height'>
                <div className='flex flex-dir-col gap-2 border-bottom px-16 py-12'>
                    <Body isBold={true} color='dark'>
                        {props.title}
                    </Body>
                    {props.subtitle && (
                        <Body color='light' size='small'>
                            {props.subtitle}
                        </Body>
                    )}
                </div>
                <div className='flex flex-dir-col flex-grow overflow-y-auto'>
                    {leaderboardContent}
                </div>
            </div>
        </Card>
    );
}
